// Lion Auto group helpers: which groups hold a section, which groups take
// one of our listings as a member (and the table's sort key for them),
// whether a selection is a group's sections,
// the listings that newly joined a group, the ones waiting for approval,
// the member ids a group's ranks count, the names new groups get, and the
// map css that lights a hovered group's sections. A
// section can be in several groups (e.g. the same sections split by the
// public-notes filter); a listing of ours is in one group per event (the
// server gives it to the group that first held it), so listingGroupsFrom
// names one group per listing, bar an older server or a stale saved result
// beside a fresh preview. Pure; tested in lionAutoGroups.test.js.
import { easternText } from './lionAutoRunner';

// The server's limit on a group name (trimmed)
export const MAX_GROUP_NAME = 60;

// How long the server flags a listing that joined a group (backend
// memberSeen.js JOINED_WINDOW_MS); only the words here use it
export const JOINED_WINDOW_HOURS = 48;

// Section ids go into a <style> element: anything outside this is skipped,
// not escaped
export const SAFE_ID = /^[A-Za-z0-9_-]+$/;

// The hovered group's outline on the map's base SVG (its own paths draw 0.5)
const HOVER_STROKE_WIDTH = 2;

const nameKey = (name) => String(name || '').trim().toLowerCase();

/** "Group N", with the first N no group of the event already uses */
export function nextGroupName(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const taken = new Set(list.map(g => nameKey(g.name)));
  let n = list.length + 1;
  while (taken.has(`group ${n}`)) n += 1;
  return `Group ${n}`;
}

/**
 * The name a duplicate of the group called `name` gets: "<name> (copy)",
 * else "<name> (copy 2)", "<name> (copy 3)"… — the first no group of the
 * event uses (any case). A long name is cut so the copy's name still fits
 * MAX_GROUP_NAME.
 */
export function copyGroupName(groups, name) {
  const taken = new Set((Array.isArray(groups) ? groups : []).map(g => nameKey(g.name)));
  const base = String(name || '').trim() || 'Group';
  const named = (n) => {
    const suffix = n === 1 ? ' (copy)' : ` (copy ${n})`;
    return `${base.slice(0, MAX_GROUP_NAME - suffix.length).trimEnd()}${suffix}`;
  };
  let n = 1;
  while (taken.has(named(n).toLowerCase())) n += 1;
  return named(n);
}

/**
 * Section id (string) → [{ groupId, groupName }], every saved group holding
 * it, in group order. Built from the groups' own criteria, so it stays right
 * after an optimistic cache patch or a delete.
 */
export function groupsBySectionFrom(groups) {
  const bySection = new Map();
  for (const g of Array.isArray(groups) ? groups : []) {
    const secs = g?.criteria?.sections;
    if (!Array.isArray(secs)) continue;
    const seen = new Set();
    for (const s of secs) {
      const sid = String(s?.sectionId);
      if (seen.has(sid)) continue;
      seen.add(sid);
      const entry = { groupId: g.id, groupName: g.name };
      const list = bySection.get(sid);
      if (list) list.push(entry);
      else bySection.set(sid, [entry]);
    }
  }
  return bySection;
}

/**
 * The css that lights one group's sections on the map: one rule, a
 * `<selectorPrefix> path[data-sid="<id>"]` selector per section (once each,
 * numeric ids as strings), with the fill, the stroke and its width. Ids that
 * fail SAFE_ID and sections with no id are skipped. '' for no group, no
 * sections, or no id left.
 * @param {{ criteria?: { sections?: { sectionId }[] } }|null} group
 * @param {{ fill: string, stroke: string, selectorPrefix?: string }} style
 * @returns {string}
 */
export function hoveredGroupCss(group, { fill, stroke, selectorPrefix } = {}) {
  const secs = group?.criteria?.sections;
  if (!Array.isArray(secs)) return '';
  const prefix = selectorPrefix ? `${selectorPrefix} ` : '';
  const ids = new Set();
  for (const s of secs) {
    const raw = s?.sectionId;
    if (raw === null || raw === undefined) continue;
    const sid = String(raw);
    if (SAFE_ID.test(sid)) ids.add(sid);
  }
  if (!ids.size) return '';
  const selectors = [...ids].map(sid => `${prefix}path[data-sid="${sid}"]`);
  return `${selectors.join(',\n')}{fill:${fill};stroke:${stroke};stroke-width:${HOVER_STROKE_WIDTH}}`;
}

/**
 * Whether the group's sections are exactly these section ids (a Set of
 * strings). A group with no sections, or an empty set, is never a match.
 */
export function hasExactSections(group, ids) {
  const secs = group?.criteria?.sections;
  if (!ids || !ids.size || !Array.isArray(secs) || !secs.length) return false;
  const own = new Set(secs.map(s => String(s?.sectionId)));
  return own.size === ids.size && [...own].every(id => ids.has(id));
}

/**
 * The name an open draft's members go under in listingGroupsFrom: its name
 * (trimmed), else the edited group's saved name, else "New group" for a new
 * one. null with no draft, or for an edited group no longer saved.
 * @param {{ id, name }|null} draft  id null = new group
 */
export function draftGroupName(groups, draft) {
  if (!draft) return null;
  const name = String(draft.name || '').trim();
  if (name) return name;
  if (draft.id == null) return 'New group';
  const saved = (Array.isArray(groups) ? groups : []).find(g => g.id === draft.id);
  return saved ? saved.name : null;
}

/**
 * Our listing's inventory id (string) → names of the groups that take it as
 * a member, from the match results. With a draft open, its live preview's
 * members stand in for the edited group's saved result, or — for a new group
 * — join under the draft's name (draftGroupName).
 * @param {object[]} groups   the event's saved groups
 * @param {object} results    { "<groupId>": MatchResult } for the saved groups
 * @param {{ id, name, members }|null} draft  the open draft (id null = new
 *   group); members = its live preview's members, null while there are none
 */
export function listingGroupsFrom(groups, results, draft = null) {
  const byListing = new Map();
  const add = (members, name) => {
    const seen = new Set();
    for (const m of Array.isArray(members) ? members : []) {
      const id = m?.inventoryId;
      if (id === null || id === undefined) continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      const list = byListing.get(key);
      if (list) list.push(name);
      else byListing.set(key, [name]);
    }
  };
  const draftName = draftGroupName(groups, draft);
  for (const g of Array.isArray(groups) ? groups : []) {
    if (draft && draft.id != null && draft.id === g.id) add(draft.members, draftName);
    else add(results?.[String(g.id)]?.members, g.name);
  }
  if (draft && draft.id == null) add(draft.members, draftName);
  return byListing;
}

/**
 * Whether one of our listings is held by a group other than the one on
 * screen (the table's "Hide other groups' listings"): it has a group, and
 * the current one is not among them. With no current group (just browsing)
 * any group counts. A listing in no group (none, or undefined) is never
 * "in another group".
 * @param {string[]|undefined} names  the listing's listingGroupsFrom entry
 * @param {string|null} currentGroup  the name the group on screen goes under
 */
export function inOtherGroupOnly(names, currentGroup) {
  if (!Array.isArray(names) || names.length === 0) return false;
  if (!currentGroup) return true;
  const current = nameKey(currentGroup);
  return !names.some(n => nameKey(n) === current);
}

/**
 * The listing table's Group sort key for one of our listings: the names of
 * the groups taking it (a listingGroupsFrom entry), trimmed and lowercased,
 * in name order ("Group 2" before "Group 10"), joined with ", " — the same
 * groups give the same key whatever order they came in. null for a listing
 * in no group (none, or undefined), which the table sorts after the rest.
 */
export function groupSortKey(names) {
  if (!Array.isArray(names) || names.length === 0) return null;
  return names
    .map(nameKey)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .join(', ');
}

/**
 * One listing that joined a group, in words: "joined Oct 13, 2026, 11:08 AM
 * ET · ranked", or "· not ranked: Auto leaves its price" (a listing outside
 * the group's ladder keeps its price, whoever runs the ladder). at: the
 * server's instant (ISO / SQLite UTC).
 */
export function joinedText(at, ranked) {
  return `joined ${easternText(at) || 'recently'} · ${ranked ? 'ranked' : 'not ranked: Auto leaves its price'}`;
}

/**
 * The "+N new" chip of a group card, or null when none joined (or the server
 * sent no `joined`: an older server). result.joined (GET /groups/match) =
 * [{ inventoryId, at }]: our listings that newly joined the group in the
 * last JOINED_WINDOW_HOURS (a new purchase, or a note / row edit that now
 * matches its filters). unranked = how many of them are not in the group's
 * ladder (criteria.ladderIds). kind 'warn' (amber) when any is unranked,
 * else 'ok' (green). title: one line per listing, named by nameOf(id) ("Sec
 * 112 Row A"; "#id" when it gives none). Repeats and entries with no id are
 * skipped.
 * @returns {{ count: number, unranked: number, title: string, kind: 'ok'|'warn' }|null}
 */
export function joinedInfo(group, result, nameOf = () => null) {
  const joined = Array.isArray(result?.joined) ? result.joined : [];
  const ladder = group?.criteria?.ladderIds;
  const ranked = new Set((Array.isArray(ladder) ? ladder : []).map(String));
  const seen = new Set();
  const lines = [];
  let unranked = 0;
  for (const j of joined) {
    const id = j?.inventoryId;
    if (id === null || id === undefined || id === '') continue;
    const key = String(id);
    if (seen.has(key)) continue;
    seen.add(key);
    const isRanked = ranked.has(key);
    if (!isRanked) unranked += 1;
    lines.push(`• ${nameOf(id) || `#${id}`}: ${joinedText(j.at, isRanked)}`);
  }
  if (!lines.length) return null;
  const count = lines.length;
  const head = `${count === 1 ? '1 listing' : `${count} listings`} joined this group in the last ${JOINED_WINDOW_HOURS} h `
    + '(a new purchase, or a note / row edit that now matches its filters):';
  return { count, unranked, title: [head, ...lines].join('\n'), kind: unranked ? 'warn' : 'ok' };
}

/**
 * The "N need approval" list of a group card, or null when none wait (or the
 * server sent no `pending`: an older server, or the look was not recorded).
 * result.pending (GET /groups/match) = [{ inventoryId, at }]: our listings
 * that joined the group after it was built and are not ranked yet, oldest
 * first; they wait (no 48 h limit) until approved, ranked by hand, left
 * unranked, or gone from the group. ids are numbers (what the review call
 * posts); entries with no usable id and repeats are skipped. label: "Sec 112
 * Row A · joined Oct 13, 2026, 11:08 AM ET", named by nameOf(id) ("#id" when
 * it gives none). ladderIds (optional): the group's saved ranking as it is
 * now; a listing in it no longer waits (the server's rule), so the card
 * drops it as soon as a save ranks it, before the matches are read again.
 * @returns {{ count: number, ids: number[], items: { inventoryId: number, name: string, at: *, label: string }[] }|null}
 */
export function pendingInfo(result, nameOf = () => null, ladderIds = null) {
  const pending = Array.isArray(result?.pending) ? result.pending : [];
  const ranked = new Set((Array.isArray(ladderIds) ? ladderIds : []).map(Number));
  const seen = new Set(ranked);
  const items = [];
  for (const p of pending) {
    const raw = p?.inventoryId;
    if (raw === null || raw === undefined || raw === '') continue;
    const id = Number(raw);
    if (!Number.isSafeInteger(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    const name = nameOf(id) || `#${id}`;
    const at = p.at ?? null;
    items.push({ inventoryId: id, name, at, label: `${name} · joined ${easternText(at) || 'recently'}` });
  }
  if (!items.length) return null;
  return { count: items.length, ids: items.map(i => i.inventoryId), items };
}

/**
 * Our listings in a group now (a match result's members, anchor included) as
 * inventory id strings, the Set ladderRanks takes: the ones the ladder's
 * ranks count, as the table numbers them. Never a member's `rank`, which is
 * the match order. An empty Set for no result, no members list or garbage;
 * members with no id are skipped.
 * @param {object|null} result  a GET /groups/match result
 * @returns {Set<string>}
 */
export function memberIdsOf(result) {
  const members = Array.isArray(result?.members) ? result.members : [];
  const ids = new Set();
  for (const m of members) {
    const id = m?.inventoryId;
    if (id === null || id === undefined || id === '') continue;
    ids.add(String(id));
  }
  return ids;
}

/**
 * A cached GET /groups/match answer with one group's pending list replaced
 * by the server's newer one (a review's answer), so the card updates without
 * another Skybox-backed match. The same object when there is nothing to
 * patch: no answer, no result for the group, or `pending` not a list.
 */
export function withPending(matches, groupId, pending) {
  const key = String(groupId);
  if (!Array.isArray(pending) || !matches?.results?.[key]) return matches;
  return { ...matches, results: { ...matches.results, [key]: { ...matches.results[key], pending } } };
}
